import React, { useCallback, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
  Typography,
  Card,
  CardContent, CardMedia,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';

import { axios, User } from 'us-web-services';
import { RoleConstants } from '../../constants';
import DateUtil from '../../util/DateUtil';

interface Props {
  canManageRoles?: boolean;
  user: User;
  setChangedEmail(changedEmail: boolean): void;
  setUser(user: User): void;
}

export default function UserEditForm({ user, canManageRoles, setChangedEmail, setUser }: Props) {
  const [active, setActive] = useState(user.active);
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [pictureUrl, setPictureUrl] = useState(user.pictureUrl);
  const [policyAccepted, setPolicyAccepted] = useState(user.policyAccepted ?? null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [superUser, setSuperUser] = useState(user.superUser);
  const [agencyAdmin, setAgencyAdmin] = useState(user.agencyAdmin);
  const [managerAdmin, setManagerAdmin] = useState(user.managerAdmin);
  const [paymentAdmin, setPaymentAdmin] = useState(user.paymentAdmin);
  const [productAdmin, setProductAdmin] = useState(user.productAdmin);
  const [securityAdmin, setSecurityAdmin] = useState(user.securityAdmin);

  const uploadErrorMessage = 'Error uploading file';

  const onDrop = useCallback(async (acceptedFiles: (string | Blob)[]) => {
    const formData = new FormData();

    if (acceptedFiles.length > 0) {
      try {
        formData.append('file', acceptedFiles[0]);
        formData.append('userId', user.id.toString());

        const uploadResponse = (await axios.post('/v2/user-pictures', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })).data.data;

        user.pictureUrl = uploadResponse.url;
        setPictureUrl(uploadResponse.url);
        setUser(user);
        toast.success('Photo uploaded successfully');
      } catch (e) {
        if (e.response?.data?.errors?.length) {
          toast.error(e.response.data.errors[0].message);
        } else {
          toast.error(uploadErrorMessage);
        }
      }
    } else {
      toast.error(uploadErrorMessage);
    }
  },
  [],
  );
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const getRoles = () => {
    const roles = [];

    if (user.parent) {
      roles.push(RoleConstants.PARENT);
    } else if (user.sitter) {
      roles.push(RoleConstants.SITTER);
    } else {
      roles.push(RoleConstants.AGENCY);
    }

    if (user.superUser) {
      roles.push(RoleConstants.SUPER_USER);

      if (user.agencyAdmin) {
        roles.push(RoleConstants.AGENCY_ADMIN);
      }

      if (user.managerAdmin) {
        roles.push(RoleConstants.MANAGER_ADMIN);
      }

      if (user.paymentAdmin) {
        roles.push(RoleConstants.PAYMENT_ADMIN);
      }

      if (user.productAdmin) {
        roles.push(RoleConstants.PRODUCT_ADMIN);
      }

      if (user.securityAdmin) {
        roles.push(RoleConstants.SECURITY_ADMIN);
      }
    }

    return roles;
  };

  const updateFirstName = event => {
    setFirstName(event.target.value);
    user.firstName = event.target.value;
    setUser(user);
  };

  const updateLastName = event => {
    setLastName(event.target.value);
    user.lastName = event.target.value;
    setUser(user);
  };

  const updateEmail = event => {
    setEmail(event.target.value);
    user.email = event.target.value;
    setChangedEmail(true);
    setUser(user);
  };

  const updatePassword = event => {
    user.password = event.target.value;
    setUser(user);
  };

  const updatePassword2 = event => {
    user.password2 = event.target.value;
    setUser(user);
  };

  const updateActive = event => {
    setActive(event.target.value);
    user.active = event.target.value;
    setUser(user);
  };

  const updateProfilePhoto = () => {
    setRemovePhoto(!removePhoto);
    user.removePhoto = !removePhoto;
    setUser(user);
  };

  const updatePolicyAccepted = moment => {
    const newPolicyAccepted = DateUtil.getDateTimeString(moment);

    setPolicyAccepted(newPolicyAccepted);
    user.policyAccepted = newPolicyAccepted;
    setUser(user);
  };

  const updateSuperUser = () => {
    setSuperUser(!superUser);
    user.superUser = !superUser;
    user.roles = getRoles();
    setUser(user);
  };

  const updatePaymentAdmin = () => {
    setPaymentAdmin(!paymentAdmin);
    user.paymentAdmin = !paymentAdmin;
    user.roles = getRoles();
    setUser(user);
  };

  const updateManagerAdmin = () => {
    setManagerAdmin(!managerAdmin);
    user.managerAdmin = !managerAdmin;
    user.roles = getRoles();
    setUser(user);
  };

  const updateSecurityAdmin = () => {
    setSecurityAdmin(!securityAdmin);
    user.securityAdmin = !securityAdmin;
    user.roles = getRoles();
    setUser(user);
  };

  const updateProductAdmin = () => {
    setProductAdmin(!productAdmin);
    user.productAdmin = !productAdmin;
    user.roles = getRoles();
    setUser(user);
  };

  const updateAgencyAdmin = () => {
    setAgencyAdmin(!agencyAdmin);
    user.agencyAdmin = !agencyAdmin;
    user.roles = getRoles();
    setUser(user);
  };

  return (
    <>
      <Typography variant='h6' gutterBottom>
        Account Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id='firstName'
            name='firstName'
            label='First name'
            fullWidth
            autoComplete='fname'
            value={firstName}
            onChange={updateFirstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id='lastName'
            name='lastName'
            label='Last name'
            fullWidth
            autoComplete='lname'
            value={lastName ?? ''}
            onChange={updateLastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id='email'
            name='email'
            label='Email'
            fullWidth
            autoComplete='email'
            value={email}
            onChange={updateEmail}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id='password'
            name='password'
            label='Password'
            fullWidth
            autoComplete='new-password'
            type='password'
            onChange={updatePassword}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id='password2'
            name='password2'
            label='Retype Password'
            fullWidth
            autoComplete='password2'
            type='password'
            onChange={updatePassword2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormLabel component='legend'>Status</FormLabel>
          <RadioGroup
            aria-label='Status'
            name='status'
            value={`${active}`}
            onChange={updateActive}
          >
            <FormControlLabel value='true' control={<Radio />} label='Active' />
            <FormControlLabel
              value='false'
              control={<Radio />}
              label='Blocked'
            />
          </RadioGroup>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateTimePicker
            inputFormat={DateUtil.dateTimeReadable}
            value={policyAccepted}
            onChange={updatePolicyAccepted}
            renderInput={(pickerProps: TextFieldProps) => (
              <TextField
                {...pickerProps}
                fullWidth
                name='policyAccepted'
                label='Policy Accepted'
                id='policyAccepted'
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormLabel component='legend'>Delete Profile photo</FormLabel>
          <Checkbox
            checked={removePhoto}
            onChange={updateProfilePhoto}
            inputProps={{
              'aria-label': 'primary checkbox',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Profile Photo
          </Typography>
          {pictureUrl ? (
            <CardMedia
              style={{
                width: 50,
                height: 50,
                marginBottom: 10,
              }}
              image={pictureUrl}
              title={`${user.firstName} picture`}
            />
          ) : (
            ''
          )}
          <section
            {...getRootProps()}
            style={{
              width: '50%',
              height: '50%',
              flex: 1,
              textAlign: 'center',
              borderWidth: 2,
              borderRadius: 2,
              borderColor: '#eeeeee',
              borderStyle: 'dashed',
              backgroundColor: '#fafafa',
              color: '#888',
              outline: 'none',
              transition: 'border .24s ease-in-out',
            }}
          >
            <input {...getInputProps()} />
            <p style={{ marginTop: 30 }}>Drag and drop a new profile photo here, or click to select one</p>
          </section>
        </Grid>
      </Grid>
      <Card style={{ marginTop: 40 }}>
        <CardContent>
          <Typography variant='h5'>
            Roles
          </Typography>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <Checkbox
                id='is-super-user'
                checked={superUser}
                disabled={!canManageRoles}
                onChange={updateSuperUser}
                inputProps={{ 'aria-label': 'Super User checkbox' }}
                style={{ padding: '0 8px 0 0' }}
              />
              <div>
                <FormLabel htmlFor='is-super-user' style={{ fontWeight: 600 }}>Super User</FormLabel>
                <Typography variant='body2' color='textSecondary'>Full admin access</Typography>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <Checkbox
                id='is-agency-admin'
                checked={agencyAdmin}
                disabled={!canManageRoles}
                onChange={updateAgencyAdmin}
                inputProps={{ 'aria-label': 'Agency Admin checkbox' }}
                style={{ padding: '0 8px 0 0' }}
              />
              <div>
                <FormLabel htmlFor='is-agency-admin' style={{ fontWeight: 600 }}>Agency Admin</FormLabel>
                <Typography variant='body2' color='textSecondary'>Manage agency accounts</Typography>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <Checkbox
                id='is-manager-admin'
                checked={managerAdmin}
                disabled={!canManageRoles}
                onChange={updateManagerAdmin}
                inputProps={{ 'aria-label': 'Manager Admin checkbox' }}
                style={{ padding: '0 8px 0 0' }}
              />
              <div>
                <FormLabel htmlFor='is-manager-admin' style={{ fontWeight: 600 }}>Manager Admin</FormLabel>
                <Typography variant='body2' color='textSecondary'>Manage team members</Typography>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <Checkbox
                id='is-payment-admin'
                checked={paymentAdmin}
                disabled={!canManageRoles}
                onChange={updatePaymentAdmin}
                inputProps={{ 'aria-label': 'Payment Admin checkbox' }}
                style={{ padding: '0 8px 0 0' }}
              />
              <div>
                <FormLabel htmlFor='is-payment-admin' style={{ fontWeight: 600 }}>Payment Admin</FormLabel>
                <Typography variant='body2' color='textSecondary'>Handle payments and refunds</Typography>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <Checkbox
                id='is-product-admin'
                checked={productAdmin}
                disabled={!canManageRoles}
                onChange={updateProductAdmin}
                inputProps={{ 'aria-label': 'Product Admin checkbox' }}
                style={{ padding: '0 8px 0 0' }}
              />
              <div>
                <FormLabel htmlFor='is-product-admin' style={{ fontWeight: 600 }}>Product Admin</FormLabel>
                <Typography variant='body2' color='textSecondary'>Configure product features</Typography>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <Checkbox
                id='is-security-admin'
                checked={securityAdmin}
                disabled={!canManageRoles}
                onChange={updateSecurityAdmin}
                inputProps={{ 'aria-label': 'Security Admin checkbox' }}
                style={{ padding: '0 8px 0 0' }}
              />
              <div>
                <FormLabel htmlFor='is-security-admin' style={{ fontWeight: 600 }}>Security Admin</FormLabel>
                <Typography variant='body2' color='textSecondary'>Reset passwords, security actions</Typography>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
